import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Switch
} from "./UI";
import { Bell, Mail, Smartphone, Check } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";

export function NotificationsSection() {
  const { addAlert } = useAlert();
  const [notifications, setNotifications] = useState({
    emailDigest: true,
    emailProjectUpdates: true,
    emailBilling: true,
    inAppMentions: true,
    inAppTasks: true,
    inAppApprovals: true,
    pushMobile: false,
    pushUrgent: true,
  });

  const updateNotification = (key, value) => {
    setNotifications({ ...notifications, [key]: value });
  };

  const handleSave = () => {
    addAlert("Your notification settings have been updated.", "success");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Notifications</h2>
        <p className="text-text-gray mt-1">Choose how and when you want to be notified.</p>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-sm-primary" />
            Email Notifications
          </CardTitle>
          <CardDescription>Control what emails you receive from SiteMaster.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-sm-border/50">
            <div>
              <p className="font-medium text-primary-text">Daily Digest</p>
              <p className="text-sm text-text-gray">Summary of activity across your projects.</p>
            </div>
            <Switch
              checked={notifications.emailDigest}
              onCheckedChange={(v) => updateNotification("emailDigest", v)}
            />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-sm-border/50">
            <div>
              <p className="font-medium text-primary-text">Project Updates</p>
              <p className="text-sm text-text-gray">New tasks, comments, and milestones.</p>
            </div>
            <Switch
              checked={notifications.emailProjectUpdates}
              onCheckedChange={(v) => updateNotification("emailProjectUpdates", v)}
            />
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-primary-text">Billing & Receipts</p>
              <p className="text-sm text-text-gray">Payment confirmations and invoices.</p>
            </div>
            <Switch
              checked={notifications.emailBilling}
              onCheckedChange={(v) => updateNotification("emailBilling", v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-sm-primary" />
            In-App Notifications
          </CardTitle>
          <CardDescription>Notifications you see inside SiteMaster.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-sm-border/50">
            <div>
              <p className="font-medium text-primary-text">Mentions</p>
              <p className="text-sm text-text-gray">When someone @mentions you.</p>
            </div>
            <Switch
              checked={notifications.inAppMentions}
              onCheckedChange={(v) => updateNotification("inAppMentions", v)}
            />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-sm-border/50">
            <div>
              <p className="font-medium text-primary-text">Task Assignments</p>
              <p className="text-sm text-text-gray">When you're assigned to a task.</p>
            </div>
            <Switch
              checked={notifications.inAppTasks}
              onCheckedChange={(v) => updateNotification("inAppTasks", v)}
            />
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-primary-text">Approval Requests</p>
              <p className="text-sm text-text-gray">When something needs your approval.</p>
            </div>
            <Switch
              checked={notifications.inAppApprovals}
              onCheckedChange={(v) => updateNotification("inAppApprovals", v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-sm-primary" />
            Push Notifications
          </CardTitle>
          <CardDescription>Mobile and desktop push alerts.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-sm-border/50">
            <div>
              <p className="font-medium text-primary-text">Mobile Push</p>
              <p className="text-sm text-text-gray">All notifications on your phone.</p>
            </div>
            <Switch
              checked={notifications.pushMobile}
              onCheckedChange={(v) => updateNotification("pushMobile", v)}
            />
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-primary-text">Urgent Only</p>
              <p className="text-sm text-text-gray">Only critical alerts and deadlines.</p>
            </div>
            <Switch
              checked={notifications.pushUrgent}
              onCheckedChange={(v) => updateNotification("pushUrgent", v)}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Check className="h-4 w-4" />
          Save Preferences
        </Button>
      </div>
    </div>
  );
}
